import { Link } from 'react-router-dom'
import { APP_NAME, PLAN_LIMITS } from '@/lib/config'
import { useEffect } from 'react'

export default function VsGoogleAnalytics() {
  useEffect(() => {
    document.title = `${APP_NAME} vs Google Analytics - Best Privacy-First Alternative`
  }, [])

  return (
    <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo-icon.png" alt={APP_NAME} className="w-8 h-8" />
                <span className="text-xl font-bold text-gray-900">{APP_NAME}</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                  Sign in
                </Link>
                <Link to="/signup" className="btn-primary">
                  Start free trial
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-primary-600 font-medium mb-4">Google Analytics Alternative</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {APP_NAME} vs Google Analytics
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Privacy-first analytics without the complexity. No personal data collection,
              no cross-site tracking, 10x lighter, and simple to set up.
            </p>
            <Link to="/signup" className="btn-primary py-3 px-8 text-base">
              Try {APP_NAME} free
            </Link>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Side-by-Side Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 text-gray-600 font-medium">Feature</th>
                    <th className="text-center py-4 px-4 text-primary-600 font-semibold">{APP_NAME}</th>
                    <th className="text-center py-4 px-4 text-gray-600 font-medium">Google Analytics</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-4 px-4 text-gray-900 font-medium">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.userinsight === 'boolean' ? (
                          row.userinsight ? (
                            <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                          )
                        ) : (
                          <span className="text-gray-900">{row.userinsight}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.ga === 'boolean' ? (
                          row.ga ? (
                            <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                          )
                        ) : (
                          <span className="text-gray-500">{row.ga}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Why Switch */}
        <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Why Switch from Google Analytics?
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Google Analytics is powerful but complex. Here's why teams choose {APP_NAME} instead.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reasons.map((reason) => (
                <div key={reason.title} className="card">
                  <div className="text-3xl mb-4">{reason.emoji}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Migration Guide */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Switch in 5 Minutes
            </h2>
            <p className="text-gray-600 text-center mb-12">
              Migrating from Google Analytics is simple. You can run both side-by-side during transition.
            </p>
            <div className="space-y-6">
              {migrationSteps.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-semibold">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gray-900 rounded-lg p-6">
              <p className="text-gray-400 text-sm mb-2">Your tracking script</p>
              <code className="text-green-400 text-sm break-all">
                {`<script async src="https://userinsight.live/tracker.js" data-site-key="YOUR_KEY"></script>`}
              </code>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-8">
              {faqs.map((faq, i) => (
                <div key={i} className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-primary-600 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to switch to privacy-first analytics?
            </h2>
            <p className="text-primary-100 mb-8">
              Start free with {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month. No credit card required.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg bg-white text-primary-600 hover:bg-primary-50 transition-colors"
            >
              Start free trial
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-gray-200 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">Home</Link>
                <Link to="/for-shopify" className="text-gray-500 hover:text-gray-700 text-sm">Shopify Analytics</Link>
                <Link to="/for-wordpress" className="text-gray-500 hover:text-gray-700 text-sm">WordPress Analytics</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

const comparisonData = [
  { feature: 'Privacy-first', userinsight: true, ga: false },
  { feature: 'GDPR compliant out of box', userinsight: true, ga: false },
  { feature: 'First-party cookies only', userinsight: true, ga: false },
  { feature: 'No personal data collected', userinsight: true, ga: false },
  { feature: 'Cookie consent usually not needed', userinsight: true, ga: false },
  { feature: 'Script size', userinsight: '<10KB', ga: '45KB+' },
  { feature: 'Session replay', userinsight: true, ga: false },
  { feature: 'Real-time dashboard', userinsight: true, ga: true },
  { feature: 'Setup time', userinsight: '2 minutes', ga: '30+ minutes' },
  { feature: 'Configuration needed', userinsight: 'None', ga: 'Complex' },
  { feature: 'Free tier', userinsight: '5K sessions/mo', ga: 'Limited' },
  { feature: 'Data retention (free)', userinsight: '90 days', ga: '14 months' },
]

const reasons = [
  {
    emoji: '🔒',
    title: 'True Privacy',
    description: 'No IP tracking, no user IDs, no cross-site tracking. First-party cookies only. Your visitors stay anonymous.',
  },
  {
    emoji: '⚖️',
    title: 'GDPR by Default',
    description: 'No complex setup for compliance. No consent banner needed in most cases. Built for privacy regulations.',
  },
  {
    emoji: '⚡',
    title: '10x Lighter',
    description: 'Under 10KB vs 45KB+. Zero impact on Core Web Vitals. Your site stays fast.',
  },
  {
    emoji: '🎯',
    title: 'Simple Dashboard',
    description: 'See what matters without wading through hundreds of reports. Clean, focused insights.',
  },
  {
    emoji: '🎬',
    title: 'Session Replay Included',
    description: 'See how users interact with your site. Understand the journey, not just the numbers.',
  },
  {
    emoji: '🚀',
    title: '2-Minute Setup',
    description: 'One script tag, auto-detects your platform. No Google Tag Manager, no configuration.',
  },
]

const migrationSteps = [
  {
    title: 'Sign up for UserInsight.live',
    description: 'Create your free account - no credit card required.',
  },
  {
    title: 'Add your website',
    description: 'Enter your domain and get your tracking script.',
  },
  {
    title: 'Install the script',
    description: 'Add one line of code to your site. Works alongside Google Analytics during transition.',
  },
  {
    title: 'Verify installation',
    description: 'Visit your site and watch the first pageview appear in real-time.',
  },
  {
    title: 'Remove Google Analytics (optional)',
    description: 'Once you\'re comfortable, remove the GA script. Your privacy-first analytics is ready.',
  },
]

const faqs = [
  {
    question: 'Can I run UserInsight.live alongside Google Analytics?',
    answer: 'Yes! Many teams run both during a transition period. UserInsight.live is lightweight and won\'t conflict with GA.',
  },
  {
    question: 'Will I lose my historical data from Google Analytics?',
    answer: 'Your GA data stays in GA. UserInsight.live starts fresh with new data. We recommend keeping GA access for historical reference.',
  },
  {
    question: 'Does UserInsight.live have all the features of Google Analytics?',
    answer: 'UserInsight.live focuses on essential metrics: visitors, pageviews, sources, and session replay. We don\'t have the hundreds of reports GA offers - by design. Most teams only use 5% of GA\'s features.',
  },
  {
    question: 'Is UserInsight.live accurate without third-party cookies?',
    answer: 'Yes. First-party cookies are actually more reliable than third-party cookies, which are increasingly blocked by browsers.',
  },
  {
    question: 'What about Google Search Console integration?',
    answer: 'Google Search Console works independently of your analytics tool. You can continue using it alongside UserInsight.live.',
  },
]
